import { useMemo } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import type { ErpModule } from '@/api/platform.api';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeatures } from '@/core/features/useFeatures';
import { useActiveModule } from '@/hooks/useActiveModule';
import { checkPermissionRule } from '@/config/routeAccessUtils';
import {
  SETTINGS_CATEGORIES,
  SETTINGS_MASTERS,
  getSettingsMastersByCategory,
  groupSettingsCategoriesByModule,
  isSettingsModuleEnabled,
  isSettingsMasterVisibleInModule,
  resolveSettingsMasterModuleKey,
  type SettingsCategory,
  type SettingsCategoryKey,
  type SettingsMaster,
  type SettingsModuleGroup,
  type SettingsModuleKey,
} from '@/config/settingsRegistry';
import { moduleSettingsViewGranted } from '@/utils/moduleSettingsAccess';

export type SettingsCategorySection = {
  category: SettingsCategory;
  masters: SettingsMaster[];
};

type SettingsMasterRoleAccess = {
  route: string;
  activeModule: ErpModule | null;
  screenGrantsConfigured: boolean;
  screenGrants: Parameters<typeof moduleSettingsViewGranted>[2];
  isMaster: boolean;
  roleCode?: string | null;
  fallbackGranted: boolean;
};

export function canViewSettingsMasterForRole({
  route,
  activeModule,
  screenGrantsConfigured,
  screenGrants,
  isMaster,
  roleCode,
  fallbackGranted,
}: SettingsMasterRoleAccess): boolean {
  if (isMaster || roleCode === 'ORGANIZATION_OWNER') return true;
  if (screenGrantsConfigured) {
    return activeModule != null && moduleSettingsViewGranted(route, activeModule, screenGrants);
  }
  return fallbackGranted;
}

export function useCanViewSettingsMaster() {
  const { has, isMaster, roleCode } = usePermissions();
  const { hasFeature } = useFeatures();
  const enabledModules = useAppSelector((s) => s.auth.enabledModules) as ErpModule[];
  const screenGrantsConfigured = useAppSelector((s) => s.session.screenGrantsConfigured);
  const screenGrants = useAppSelector((s) => s.session.screenGrants);
  const activeModule = useActiveModule();

  return (master: SettingsMaster) => {
    if (!isSettingsModuleEnabled(resolveSettingsMasterModuleKey(master), enabledModules)) return false;
    if (master.feature && !hasFeature(master.feature)) return false;
    return canViewSettingsMasterForRole({
      route: master.route,
      activeModule,
      screenGrantsConfigured,
      screenGrants,
      isMaster,
      roleCode,
      fallbackGranted: checkPermissionRule(master.access.view, has),
    });
  };
}

export function useActiveSettingsModuleKey(): SettingsModuleKey {
  const activeModule = useActiveModule();
  return activeModule ?? 'HR';
}

export function useVisibleSettingsMasters(category?: string): SettingsMaster[] {
  const canView = useCanViewSettingsMaster();
  return useMemo(() => {
    const masters = category ? getSettingsMastersByCategory(category as SettingsCategoryKey) : SETTINGS_MASTERS;
    return masters.filter(canView);
  }, [canView, category]);
}

/** Module masters plus platform-wide organization, system, etc. */
export function useModuleScopedSettingsMasters(): SettingsMaster[] {
  const scopeModule = useActiveSettingsModuleKey();
  const visibleMasters = useVisibleSettingsMasters();
  return useMemo(
    () => visibleMasters.filter((master) => isSettingsMasterVisibleInModule(master, scopeModule)),
    [scopeModule, visibleMasters],
  );
}

export function useVisibleSettingsCategories(): SettingsCategory[] {
  const canView = useCanViewSettingsMaster();
  return useMemo(
    () =>
      SETTINGS_CATEGORIES
        .filter((category) => getSettingsMastersByCategory(category.key).some(canView))
        .sort((a, b) => a.order - b.order),
    [canView],
  );
}

/** Category sections for the active module — heading + masters on one settings page. */
export function useModuleSettingsSections(): SettingsCategorySection[] {
  const masters = useModuleScopedSettingsMasters();
  return useMemo(() => {
    const categoryKeys = [...new Set(masters.map((master) => master.category))];
    return SETTINGS_CATEGORIES.filter((category) => categoryKeys.includes(category.key))
      .sort((a, b) => a.order - b.order)
      .map((category) => ({
        category,
        masters: masters.filter((master) => master.category === category.key),
      }))
      .filter((section) => section.masters.length > 0);
  }, [masters]);
}

export function useVisibleSettingsModuleGroups(): SettingsModuleGroup[] {
  const categories = useVisibleSettingsCategories();
  return useMemo(() => groupSettingsCategoriesByModule(categories), [categories]);
}
